// Team card + owner/contributor circles for Projects > Updates.
// Everything reads one selected-week projection (weeklyTeam) shared through
// context, so the card, posting row, section owners and contributors agree.
import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { T } from "../../../app/tokens.js";
import { useHoverIntent } from "../../../components/useHoverIntent.js";
import { InternalLink } from "../../../components/InternalLink.jsx";
import { fmtIsoDate } from "./weeklyModel.js";
import { TEAM_ROLES } from "../../../lib/weeklyTeam.js";

export const TeamContext = createContext(null);
export const useTeam = () => useContext(TeamContext);

const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
const monD = (iso) => { const d = typeof iso === "string" ? fmtIsoDate(iso.slice(0, 10)) : ""; return d ? d.replace(/, \d{4}$/, "") : ""; };
// Exact organisation only; anything else is never guessed into a colour.
function orgColor(org) {
  const o = String(org || "").trim().toLowerCase();
  return o === "jll" ? T.negotiateOrange : o === "ripple" ? T.blue : null;
}
const roleOf = (key) => TEAM_ROLES.find((r) => r.key === key);

export function postedText(p) {
  if (p.posted === true) return `posted ${monD(p.postedAt) || "this week"}`;
  if (p.posted === false) return `not posted this week${p.lastPostedAt ? ` · last ${monD(p.lastPostedAt)}` : ""}`;
  return "posting history unavailable";
}
export function personLabel(p) {
  const role = p.label || "Team member";
  return [p.displayName || "Name unavailable", `${role}${p.org ? `, ${p.org}` : ", organisation unknown"}`, p.sections, postedText(p)].filter(Boolean).join(" · ");
}

function Tip({ anchor, text }) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const r = anchor.current?.getBoundingClientRect();
    if (r) setPos({ left: Math.min(Math.max(r.left + r.width / 2, 130), (window.innerWidth || 1024) - 130), top: r.top - 6 });
  }, [anchor]);
  if (!pos || typeof document === "undefined") return null;
  return createPortal(
    <span role="tooltip" data-testid="team-circle-tip" style={{ position: "fixed", left: pos.left, top: pos.top, transform: "translate(-50%, -100%)", maxWidth: 260, background: T.dark, color: T.card, fontSize: T.fsXxs, fontWeight: T.fwSemibold, lineHeight: 1.4, padding: "6px 9px", borderRadius: 7, zIndex: 1000, pointerEvents: "none", whiteSpace: "normal" }}>{text}</span>,
    document.body,
  );
}

// One focusable circle. `faded` applies only to the inner visual.
export function PersonCircle({ person, label, size = 22, faded = false, unassigned = false, unknown = false, testId }) {
  const intent = useHoverIntent();
  const ref = useRef(null);
  const [broken, setBroken] = useState(false);
  const team = useTeam();
  const photo = team?.photosAllowed ? person?.photoUrl : null;
  useEffect(() => { setBroken(false); }, [photo]);
  const reset = intent.reset;
  useEffect(() => { reset(); }, [label, reset]);
  const color = person ? orgColor(person.org) : null;
  const visual = unassigned || unknown
    ? { background: "transparent", color: T.light, border: `1.5px dashed ${T.light}` }
    : color ? { background: color, color: T.white, border: "1.5px solid transparent" }
    : { background: "transparent", color: T.dark, border: `1.5px solid ${T.mid}` };
  return (
    <span ref={ref} {...intent.triggerProps} className="wk-av" tabIndex={0} role="img" aria-label={label} data-testid={testId}
      data-faded={faded ? "true" : undefined} style={{ flexShrink: 0 }}>
      <span data-testid={testId ? `${testId}-visual` : undefined} style={{ width: size, height: size, borderRadius: "50%", boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", overflow: "hidden", fontSize: T.fsXxs, fontWeight: T.fwBold, opacity: faded ? 0.4 : 1, ...visual }}>
        {photo && !broken && !unassigned && !unknown
          ? <img src={photo} alt="" onError={() => setBroken(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : unassigned ? "?" : unknown ? "--" : initials(person?.displayName)}
      </span>
      {intent.open && <Tip anchor={ref} text={label} />}
    </span>
  );
}

// Section owner circle(s), right side of a section header. No names/roles.
export function OwnerCircles({ roleKey, testId }) {
  const team = useTeam();
  const role = roleOf(roleKey);
  if (!team || !role) return null;
  const id = testId || `owner-${roleKey}`;
  let body;
  if (!team.assignmentsAvailable) {
    body = <PersonCircle unknown testId={`${id}-unknown`} label={`${role.label} · assignment not captured for this week`} />;
  } else {
    const owners = team.owners[roleKey] || [];
    body = owners.length
      ? owners.map((p) => <PersonCircle key={p.rowKey} person={p} faded={p.posted !== true} testId={`${id}-${p.personId}`} label={personLabel(p)} />)
      : <PersonCircle unassigned testId={`${id}-unassigned`} label={`${role.label} · Unassigned · ${role.sections}`} />;
  }
  return <span data-testid={id} style={{ display: "inline-flex", gap: 4, alignItems: "center", flexShrink: 0 }}>{body}</span>;
}

// Small author circle at an item's right, only for a stored non-owner author.
export function ContributorCircle({ item, roleKey, testId }) {
  const team = useTeam();
  // Unknown ownership cannot prove a non-owner author.
  const person = team?.assignmentsAvailable ? team.contributor(item, roleKey) : null;
  if (!person || !team.assignmentsAvailable) return null;
  return <PersonCircle person={person} size={20} faded={false} testId={testId} label={personLabel(person)} />;
}

function AssignLink({ projectId, onGuardedNavigate }) {
  const bypass = useRef(false);
  const ref = useRef(null);
  const onClick = (e) => {
    if (bypass.current) { bypass.current = false; return; }
    if (!onGuardedNavigate || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return;
    e.preventDefault();
    onGuardedNavigate(() => { bypass.current = true; ref.current?.querySelector("a")?.click(); });
  };
  return (
    <span ref={ref}><InternalLink href={`/projects/team?participantsProject=${encodeURIComponent(projectId)}`} onClick={onClick}
      data-testid={`team-assign-${projectId}`} style={{ color: T.blue, fontWeight: T.fwSemibold, fontSize: T.fsXs, textDecoration: "none", whiteSpace: "nowrap" }}>Assign</InternalLink></span>
  );
}

export function TeamCard({ project, canAssign, onGuardedNavigate }) {
  const team = useTeam();
  if (!team) return null;
  const gaps = Array.isArray(project.teamEvidence?.gaps) ? project.teamEvidence.gaps.filter((g) => typeof g === "string" && g) : [];
  const count = team.total == null ? "unavailable" : team.posted == null ? `${team.total} ${team.total === 1 ? "person" : "people"}` : `${team.posted} of ${team.total} posted`;
  return (
    <section className="wk-ecard" data-testid="updates-team-card" aria-label="Team" style={{ marginBottom: 12 }}>
      <header className="wk-ecard-hd" style={{ padding: "10px 15px" }}>
        <h3 style={{ margin: 0, fontSize: T.fsBase, fontWeight: T.fwRegular, color: T.dark }}>Team</h3>
        <span data-testid="team-posted-count" style={{ fontSize: T.fsXs, color: T.mid, fontVariantNumeric: "tabular-nums" }}>{count}</span>
      </header>
      <div style={{ padding: "4px 15px 8px" }}>
        {!team.assignmentsAvailable ? (
          <div data-testid="team-unavailable" style={{ fontSize: T.fsXs, color: T.mid, fontStyle: "italic", padding: "8px 0" }}>Team assignments were not captured for this week.</div>
        ) : team.rows.map((r, i) => {
          const inactive = !r.unassigned && (r.active === false || r.postEligible === false);
          const status = r.unassigned ? null : inactive ? [r.active === false ? "Person inactive · not counted" : "Account inactive · not counted", T.light] : r.posted === true ? ["Posted", T.green] : r.posted === false ? [r.lastPostedAt ? `Last ${monD(r.lastPostedAt)}` : "Not posted", T.light] : ["History unavailable", T.light];
          return (
            <div key={r.rowKey} data-testid={`team-row-${r.rowKey}`} data-role={r.key} data-posted={r.unassigned ? "unassigned" : String(r.posted)}
              style={{ display: "grid", gridTemplateColumns: "26px minmax(0, 1fr) auto", gap: 10, alignItems: "center", padding: "7px 0", borderTop: i ? `1px solid ${T.lightGray}` : 0 }}>
              {r.unassigned
                ? <PersonCircle unassigned size={26} testId={`team-circle-${r.rowKey}`} label={`${r.label} · Unassigned · ${r.sections}`} />
                : <PersonCircle person={r} size={26} faded={inactive || r.posted !== true} testId={`team-circle-${r.rowKey}`} label={personLabel(r)} />}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: T.fsXs, color: r.unassigned ? T.light : T.dark }} title={r.displayName || undefined}>{r.unassigned ? "Unassigned" : r.displayName || "Name unavailable"}{r.isPrimary && <span style={{ marginLeft: 4, color: T.mid, fontSize: T.fsXxs }}>primary</span>}</div>
                <div style={{ fontSize: T.fsXxs, color: T.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label} · {r.sections}</div>
              </div>
              {r.unassigned
                ? canAssign ? <AssignLink projectId={project.id} onGuardedNavigate={onGuardedNavigate} /> : <span />
                : <span data-testid={`team-status-${r.rowKey}`} style={{ fontSize: T.fsXxs, color: status[1], whiteSpace: "nowrap" }}>{status[0]}</span>}
            </div>
          );
        })}
        {!team.photosAllowed && team.rows.some((r) => r.photoUrl) && <div data-testid="team-photos-historical" style={{ fontSize: T.fsXxs, color: T.light, paddingTop: 6 }}>Historical photos are unavailable; initials shown.</div>}
        {gaps.length > 0 && (
          <details data-testid="team-gaps" style={{ fontSize: T.fsXxs, color: T.mid, paddingTop: 6 }}>
            <summary style={{ cursor: "pointer", color: T.light }}>Evidence gaps ({gaps.length})</summary>
            <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>{gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
          </details>
        )}
      </div>
    </section>
  );
}
